/**
 * PremiumSidebar — Linear/Rippling/Deel style dark navy sidebar
 * Reuses the existing navigationConfig from AccordionSidebar (single source of truth)
 * but applies the new premium SaaS visual language.
 */
import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePlan } from '../../contexts/PlanContext';
import { useWipModules } from '../../hooks/useWipModules';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard, Users, Network, BookUser, Brain, Target, BarChart3,
  Database, User, LogOut, ChevronDown, Award, FolderKanban, Sparkles,
  Calendar, CalendarDays, Gauge, Upload, Settings, CreditCard, Crown,
  ClipboardCheck, BookOpen, Briefcase, Compass, Send, Receipt,
  Calculator, FileSignature, Wallet, Lock, ArrowUpRight, UserPlus,
} from 'lucide-react';

// Use a slim, focused config tailored for the premium shell.
// (Stays in sync with AccordionSidebar — both reflect the same modules)
const NAV = [
  {
    section: null,
    items: [
      { id: 'dashboard', moduleId: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard, path: '/dashboard',
        roles: ['SuperAdmin', 'Admin', 'Manager', 'Employé'] },
    ],
  },
  {
    section: 'Pilotage',
    items: [
      { id: 'organisation', moduleId: 'employees', label: 'Organisation', icon: Users, roles: ['SuperAdmin', 'Admin'],
        children: [
          { label: 'Employés', path: '/employees', icon: Users, moduleId: 'employees' },
          { label: 'Organigramme', path: '/orgchart', icon: Network, moduleId: 'orgchart' },
          { label: 'Trombinoscope', path: '/directory', icon: BookUser, moduleId: 'directory' },
          { label: 'Fiches de poste', path: '/skills/profiles', icon: Award, moduleId: 'skills' },
        ] },
      { id: 'talents', moduleId: 'skills', label: 'Talents', icon: Brain, roles: ['SuperAdmin', 'Admin'],
        children: [
          { label: 'Cockpit Compétences', path: '/skills/cockpit', icon: Gauge, moduleId: 'skills' },
          { label: 'Bibliothèque', path: '/skills', icon: FolderKanban, moduleId: 'skills' },
          { label: 'Référentiel carrière', path: '/career/referential', icon: Briefcase, moduleId: 'career' },
          { label: 'Career Intelligence', path: '/career/intelligence', icon: Sparkles, moduleId: 'career' },
          { label: 'Mobilité interne', path: '/career/mobility', icon: Send, moduleId: 'career' },
          { label: 'Plans de succession', path: '/career/succession', icon: Crown, moduleId: 'career' },
        ] },
      { id: 'performance', moduleId: 'performance', label: 'Performance', icon: Target, roles: ['SuperAdmin', 'Admin'],
        children: [
          { label: 'Campagnes', path: '/evaluation-campaigns', icon: Calendar, moduleId: 'performance' },
          { label: 'Reporting RH', path: '/performance-report', icon: BarChart3, moduleId: 'performance' },
        ] },
      { id: 'absences', moduleId: 'absences', label: 'Absences', icon: Calendar, roles: ['SuperAdmin', 'Admin', 'Manager'],
        children: [
          { label: 'Gestion', path: '/absences/admin', icon: ClipboardCheck, moduleId: 'absences' },
          { label: 'Calendrier', path: '/absences/calendar', icon: Calendar, moduleId: 'absences' },
          { label: 'Dashboard RH', path: '/absences/dashboard', icon: BarChart3, moduleId: 'absences' },
          { label: 'Analytics & Paie', path: '/absences/analytics', icon: BarChart3, moduleId: 'absences' },
        ] },
      { id: 'paie', moduleId: 'payroll', label: 'Paie', icon: Wallet, roles: ['SuperAdmin', 'Admin'],
        children: [
          { label: 'Cycles de paie', path: '/payroll/runs', icon: Calculator, moduleId: 'payroll' },
          { label: 'Contrats', path: '/payroll/contracts', icon: FileSignature, moduleId: 'payroll' },
          { label: 'Rubriques', path: '/payroll/rubrics', icon: Receipt, moduleId: 'payroll' },
        ] },
    ],
  },
  {
    section: 'Données',
    items: [
      { id: 'analyse', moduleId: 'analytics', label: 'Analyse', icon: BarChart3, roles: ['SuperAdmin', 'Admin', 'Manager'],
        children: [
          { label: 'Indicateurs RH', path: '/hr-indicators', icon: BarChart3, moduleId: 'analytics' },
          { label: 'Insights IA', path: '/ai-insights', icon: Sparkles, moduleId: 'analytics' },
        ] },
      { id: 'donnees', moduleId: 'import', label: 'Données', icon: Database, roles: ['SuperAdmin', 'Admin'],
        children: [
          { label: 'Import', path: '/import', icon: Upload, moduleId: 'import' },
          { label: 'Prospects (Démo/Devis)', path: '/prospects', icon: Sparkles, roles: ['SuperAdmin'] },
        ] },
    ],
  },
  {
    section: 'Mon compte',
    items: [
      { id: 'profile', label: 'Mon profil', icon: User, path: '/profile',
        roles: ['SuperAdmin', 'Admin', 'Manager', 'Employé'] },
      { id: 'my-absences', moduleId: 'absences', label: 'Mes absences', icon: CalendarDays, path: '/absences/me',
        roles: ['SuperAdmin', 'Admin', 'Manager', 'Employé'] },
      { id: 'my-payslips', moduleId: 'payroll', label: 'Mes bulletins', icon: Wallet, path: '/payroll/my',
        roles: ['SuperAdmin', 'Admin', 'Manager', 'Employé'] },
      { id: 'my-career', moduleId: 'career', label: 'Ma carrière', icon: Compass, path: '/career/profile',
        roles: ['SuperAdmin', 'Admin', 'Manager', 'Employé'] },
      { id: 'my-evaluations', moduleId: 'performance', label: 'Mes évaluations', icon: Target, path: '/my-evaluations',
        roles: ['SuperAdmin', 'Admin', 'Manager', 'Employé'] },
      { id: 'settings', moduleId: 'settings', label: 'Paramètres', icon: Settings, path: '/settings',
        roles: ['SuperAdmin', 'Admin'] },
      { id: 'billing', moduleId: 'billing', label: 'Facturation', icon: CreditCard, path: '/billing/subscription',
        roles: ['Admin'] },
      { id: 'guide', moduleId: 'user_guide', label: 'Guide utilisateur', icon: BookOpen, path: '/user-guide',
        roles: ['SuperAdmin', 'Admin', 'Manager', 'Employé'] },
    ],
  },
];

function userMatchesRoles(user, roles) {
  if (!roles || roles.length === 0) return true;
  return roles.includes(user?.role) || (user?.role === 'Viewer' && roles.includes('Employé'));
}

function NavLeaf({ item, activePath, onNav, wipBlocked }) {
  const Icon = item.icon;
  const isActive = !wipBlocked && (activePath === item.path || activePath.startsWith(item.path + '/'));

  if (wipBlocked) {
    return (
      <div
        data-testid={`nav-${item.id || item.label.toLowerCase().replace(/\s+/g, '-')}-wip`}
        aria-disabled="true"
        title="Module en cours de finalisation — bientôt disponible"
        className="group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 cursor-not-allowed select-none"
      >
        <Icon className="h-[18px] w-[18px] shrink-0 text-slate-600" />
        <span className="truncate flex-1">{item.label}</span>
        <span className="ml-auto text-[9px] font-bold tracking-wider text-amber-400/70 bg-amber-400/10 px-1.5 py-0.5 rounded">
          BIENTÔT
        </span>
      </div>
    );
  }

  return (
    <Link to={item.path} onClick={onNav}
      data-testid={`nav-${item.id || item.label.toLowerCase().replace(/\s+/g, '-')}`}
      className={cn(
        'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all',
        isActive
          ? 'bg-white/10 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.07)]'
          : 'text-slate-400 hover:text-white hover:bg-white/[0.06]'
      )}>
      {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r bg-blue-400" />}
      <Icon className={cn('h-[18px] w-[18px] shrink-0 transition-colors',
        isActive ? 'text-white' : 'text-slate-400 group-hover:text-slate-200')} />
      <span className="truncate">{item.label}</span>
    </Link>
  );
}

function NavParent({ item, activePath, onNav, isModuleBlocked }) {
  const Icon = item.icon;
  const hasChildren = !!item.children;
  const childActive = hasChildren && item.children.some(c => activePath === c.path || activePath.startsWith(c.path.split('?')[0] + '/'));
  const [open, setOpen] = useState(childActive);
  useEffect(() => { if (childActive) setOpen(true); }, [childActive]);

  // Si tous les enfants sont WIP, le parent l'est aussi (visuellement)
  const childrenBlocked = hasChildren
    ? item.children.map(c => isModuleBlocked(c.moduleId))
    : [];
  const allChildrenBlocked = hasChildren && childrenBlocked.length > 0 && childrenBlocked.every(Boolean);
  const parentBlocked = isModuleBlocked(item.moduleId) || allChildrenBlocked;

  if (!hasChildren) {
    return <NavLeaf item={item} activePath={activePath} onNav={onNav} wipBlocked={isModuleBlocked(item.moduleId)} />;
  }

  return (
    <div className="space-y-0.5">
      <button onClick={() => setOpen(o => !o)}
        data-testid={`nav-toggle-${item.id}`}
        className={cn(
          'group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all',
          parentBlocked
            ? 'text-slate-600'
            : childActive
              ? 'text-white'
              : 'text-slate-400 hover:text-white hover:bg-white/[0.06]'
        )}>
        <Icon className={cn('h-[18px] w-[18px] shrink-0',
          parentBlocked
            ? 'text-slate-600'
            : childActive ? 'text-white' : 'text-slate-400 group-hover:text-slate-200')} />
        <span className="truncate flex-1 text-left">{item.label}</span>
        {parentBlocked && (
          <span className="text-[9px] font-bold tracking-wider text-amber-400/70 bg-amber-400/10 px-1.5 py-0.5 rounded">
            BIENTÔT
          </span>
        )}
        <ChevronDown className={cn('h-3.5 w-3.5 text-slate-500 transition-transform',
          open && 'rotate-180')} />
      </button>
      {open && (
        <div className="ml-3 space-y-0.5 border-l border-white/[0.06] pl-3">
          {item.children.map((c) => (
            <NavLeaf key={c.path} item={{ ...c, id: c.path }} activePath={activePath} onNav={onNav}
              wipBlocked={isModuleBlocked(c.moduleId)} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function PremiumSidebar({ onClose, onLogout }) {
  const { user } = useAuth();
  const { isPro } = usePlan();
  const { isModuleBlocked } = useWipModules();
  const location = useLocation();
  const activePath = location.pathname + (location.search || '');

  const sections = useMemo(() => {
    return NAV.map(sec => ({
      ...sec,
      items: sec.items
        .map((it) => {
          // Filter children by role too — keep parent only if at least one child remains
          if (it.children) {
            const visibleChildren = it.children.filter((c) => userMatchesRoles(user, c.roles));
            return { ...it, children: visibleChildren };
          }
          return it;
        })
        .filter((it) => userMatchesRoles(user, it.roles))
        .filter((it) => !it.children || it.children.length > 0),
    })).filter(sec => sec.items.length > 0);
  }, [user]);

  const initials = ((user?.firstName?.[0] || '') + (user?.lastName?.[0] || '')).toUpperCase() || 'U';
  const companyName = user?.companyName || 'HR Engine';

  return (
    <div className="flex h-full w-[260px] flex-col bg-[#0B1437] text-slate-100" data-testid="premium-sidebar">
      {/* Brand block */}
      <div className="flex items-center gap-3 px-5 py-5 border-b border-white/[0.06]">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-[0_8px_24px_-8px_rgba(59,130,246,0.6)]">
          <Sparkles className="h-5 w-5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-white truncate" data-testid="sidebar-company-name">
            {companyName}
          </div>
          <div className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-white/[0.06] px-2 py-0.5 text-[10px] font-medium tracking-wider text-slate-300">
            {isPro ? <Crown className="h-3 w-3 text-amber-400" /> : <Lock className="h-3 w-3 text-slate-400" />}
            Plan {isPro ? 'BUSINESS' : 'STARTER'}
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-white/10">
        {sections.map((sec, i) => (
          <div key={sec.section || `s${i}`}>
            {sec.section && (
              <div className="mb-2 px-3 text-[10px] font-semibold tracking-[0.14em] text-slate-500 uppercase">
                {sec.section}
              </div>
            )}
            <div className="space-y-0.5">
              {sec.items.map(it => (
                <NavParent key={it.id} item={it} activePath={activePath} onNav={onClose} isModuleBlocked={isModuleBlocked} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* User */}
      <div className="border-t border-white/[0.06] p-3">
        <div className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-white/[0.04] transition-colors">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-xs font-bold text-white ring-1 ring-white/10">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-white truncate" data-testid="sidebar-user-name">
              {user?.firstName} {user?.lastName}
            </div>
            <div className="text-[11px] text-slate-400 truncate capitalize">
              {user?.role === 'Viewer' ? 'Employé' : user?.role}
            </div>
          </div>
          <button onClick={onLogout} data-testid="sidebar-logout-btn"
            className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.08] transition">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
